import { Component, OnInit } from '@angular/core';
import { currency } from 'ng-brazil/currency/validator';
import { Auth } from 'src/app/models/auth';
import { ChartData } from 'src/app/models/chart-data';
import { GraficoHome } from 'src/app/models/grafico-home';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { GraficoHomeService } from 'src/app/services/grafico-home.service';
import { NewAlertService } from 'src/app/shared/components/new-alert/new-alert.service';
import { ApiError } from 'src/app/shared/enums/api-error.enum';

@Component({
  selector: 'app-chart',
  templateUrl: './chart.component.html',
  styleUrls: ['./chart.component.css']
})
export class ChartComponent implements OnInit {

  public currentUser: Auth;
  public chartData: GraficoHome[];
  public months = [];
  public labels = [];
  public chart = [];
  public ready = false;
  public monthLabels = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

  public type = 'line';
  public data = {
    labels: this.labels,
    datasets: this.chart
  };

  public options = {
    responsive: true,
    maintainAspectRatio: true,
    tooltips: {
      callbacks: {
        label: (tooltipItem, data) => {
          let label = data.datasets[tooltipItem.datasetIndex].label || '';

          if (label) {
              label += ': ';
          }
          if (tooltipItem.yLabel > 0) {
            label += new Intl.NumberFormat('pt', { style: 'currency', currency: 'BRL' }).format(tooltipItem.yLabel);
          } else {
            label += 'R$ 0,00';
          }
          return label;
        }
      }
    },
    legend: {
      position: 'bottom',
      labels: {
        boxWidth: 10,
        fontFamily: 'ubuntu',
        fontColor: '#707070'
      },
    },
    scales: {
      xAxes: [{
        gridLines: {
            display: true
        }
      }],
      yAxes: [{
        gridLines: {
            display: false
        },
        ticks: {
          // Include a dollar sign in the ticks
          callback(value, index, values) {
              if (value > 0) {
                return new Intl.NumberFormat('pt', { style: 'currency', currency: 'BRL' }).format(value);
              }
              return 'R$ 0,00';
          }
      }
      }]
    }
  };

  constructor(private chartService: GraficoHomeService,
              private alertService: NewAlertService,
              private authenticationService: AuthenticationService) { }

  ngOnInit() {
    this.setRange();
    this.currentUser = this.authenticationService.currentUserValue;

    this.chartService.getDashboardOrders(this.currentUser.token.assignorGuid).subscribe(
      response => {
        if (response.status === ApiError.Sucesso) {
          this.chartData = response.result;
          const ids = [];
          const group = this.chartData.reduce((aux, item) => {
            if (!aux[item.produtoID]) {
              aux[item.produtoID] = [];
              ids.push(item.produtoID);
            }

            aux[item.produtoID].push(item);
            return aux;
          }, {});

          const colorArray = [
            'rgb(212, 61, 66)',
            'rgb(255, 145, 0)',
            'rgb(0, 84, 166)',
          ];

          let colors = this.shuffle(this.interpolateManyColors(colorArray, (ids.length/(colorArray.length-1))+1));

          ids.forEach((item, i) => {
            const data = new ChartData();
            group[item].forEach((element: GraficoHome) => {
              data.data[this.months.indexOf(element.dataPedido)] = element.valorPedido;
              data.label = element.nomeProduto;
              data.backgroundColor = colors[i];
              data.borderColor = colors[i];
            });
            data.data.reverse();
            this.chart.push(data);
          });
          this.ready = true;

        } else {
          this.alertService.error(response.message);
        }
      }
    );
  }

  setRange() {
    const today = new Date();

    let month = today.getMonth() + 1;
    let year = today.getFullYear();

    for (let i = 12; i > 0; i--) {
        month--;
        if (month === -1) {
            month += 12;
            year--;
        }
        this.months.push(`${month + 1}/${year}`);
        this.labels.push(this.monthLabels[month]);
    }

    this.labels.reverse();
  }

  shuffle(o) {
    for (var j, x, i = o.length; i; j = Math.floor(Math.random() * i), x = o[--i], o[i] = o[j], o[j] = x);
    return o;
  }

  interpolateManyColors(colors: string[], stepsBetweenEach: number): string[] {
    let final: string[] = [];
    if (stepsBetweenEach < 2) {
      stepsBetweenEach = 2;
    }
  
    for (let i = 0; i < colors.length; i += 1) {
      if (colors[i] && colors[i + 1]) {
        const c = this.interpolateColors(colors[i], colors[i + 1], stepsBetweenEach);
        if (c) {
          // The first item of subsequent sets will be the same as the last item of the previous step
          if (i > 0) {
            c.shift();
          }
          final.push(...c);
        }
      }
    }
  
    return final;
  }

  interpolateColors(
    color1: string,
    color2: string,
    steps: number,
    easing?: Function,
  ): string[] | undefined {
    if (!color1 || !color2 || !steps) {
      return;
    }
    const interpolatedColorArray: string[] = [];
    const stepFactor: number = 1 / (steps - 1);
    const color1Strings: RegExpMatchArray | null = color1.match(/\d+/g);
    const color2Strings: RegExpMatchArray | null = color2.match(/\d+/g);
    const color1Numbers: number[] | null = color1Strings ? color1Strings.map(Number) : null;
    const color2Numbers: number[] | null = color2Strings ? color2Strings.map(Number) : null;
  
    if (!color1Numbers || !color2Numbers) {
      return;
    }
  
    for (let i = 0; i < steps; i++) {
      const color = this.interpolateColor(color1Numbers, color2Numbers, stepFactor * i, easing);
      interpolatedColorArray.push(`rgb(${color})`);
    }
  
    return interpolatedColorArray;
  }
  
  interpolateColor(color1: number[], color2: number[], factor = .5, easing?: Function): number[] {
    const result: number[] = [];
    const factorInc = easing ? easing(factor) : factor;
  
    for (let i = 0; i < color1.length; i++) {
      result.push(Math.round(color1[i] + factorInc * (color2[i] - color1[i])));
    }
  
    return result;
  }
}
